import { useEffect, useMemo, useState } from "react";
import AppLayout from "../../../../shared/components/AppLayout";
import Button from "../../../../shared/components/Button";
import EmptyState from "../../../../shared/components/EmptyState";
import LoadingPage from "../../../../shared/components/LoadingPage";
import { siteTexts } from "../../../../shared/i18n/siteTexts";
import { buildLoginHref } from "../../../../shared/lib/loginRedirect";
import { formatEventDateRangeLabel } from "../../../../shared/utils/dateTime";
import { useAuth } from "../../../auth/shared/hooks/useAuth";
import { listPublicChallenges } from "../../../challenges/services/challengeAdminService";
import type { ChallengeHistoryItem } from "../../../challenges/types/challengeAdmin";
import { challengeTypeLabel } from "../../../challenges/utils/challengeLabels";
import { listPublicMatches } from "../../../matches/shared/services/matchesService";
import type { OpenMatchItem } from "../../../matches/shared/types/matches";
import {
  formatOpenMatchCategories,
  formatOpenMatchScheduleRange,
  listUpcomingOpenMatchesForClub,
  resolveOpenMatchAvailableSlots,
  resolveOpenMatchDisplayTitle,
} from "../../../matches/shared/utils/openMatchPresentation";
import ClubActionButtons from "../components/ClubActionButtons";
import ClubDetailMap from "../components/ClubDetailMap";
import ClubDetailTabs, {
  type ClubDetailSection,
} from "../components/ClubDetailTabs";
import { getClubBySlug } from "../../shared/services/clubsService";
import type { Club } from "../../shared/types/club";
import { canResolveClubMapPosition } from "../../shared/utils/clubMapPosition";
import "./ClubsPage.css";

type Props = {
  slug: string;
};

function contactItems(club: Club) {
  const t = siteTexts.clubs.detail;
  return [
    { label: t.phone, value: club.phone },
    { label: t.whatsapp, value: club.whatsapp },
    { label: t.email, value: club.email },
  ].filter((item) => item.value);
}

function clubAddressParts(club: Club) {
  return [
    club.address,
    club.number,
    club.neighborhood,
    club.city,
    club.state,
    club.country,
  ].filter(Boolean);
}

function buildClubMapEmbedUrl(club: Club) {
  const address = clubAddressParts(club).join(", ");
  if (!address) return "";
  return `https://www.google.com/maps?q=${encodeURIComponent(address)}&output=embed`;
}

function normalizeSearchText(value: unknown) {
  return String(value ?? "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function eventAddressCandidates(item: ChallengeHistoryItem) {
  return [
    item.address,
    ...(Array.isArray(item.registrationCategories)
      ? item.registrationCategories.map((category) => category.address)
      : []),
  ];
}

function isUpcomingClubEvent(item: ChallengeHistoryItem, club: Club, now: number) {
  if (item.status === "finished") return false;
  const startAt = new Date(String(item.scheduledAt || "")).getTime();
  if (!Number.isFinite(startAt)) return false;
  const endAt = new Date(String(item.scheduledEndAt || item.scheduledAt || "")).getTime();
  if (Number.isFinite(endAt) && endAt < now) return false;

  const clubName = normalizeSearchText(club.name);
  const clubCity = normalizeSearchText(club.city);
  const clubState = normalizeSearchText(club.state);
  const eventCity = normalizeSearchText(item.city);
  const eventState = normalizeSearchText(item.state);
  const locationMatches =
    (!clubCity || !eventCity || clubCity === eventCity) &&
    (!clubState || !eventState || clubState === eventState);

  if (!clubName) return false;
  return eventAddressCandidates(item).some((address) => {
    const normalizedAddress = normalizeSearchText(address);
    return normalizedAddress.includes(clubName) && locationMatches;
  });
}

function eventDetailHref(item: ChallengeHistoryItem) {
  const routeBase = item.challengeType === "tournament" ? "torneios" : "desafios";
  return `#/${routeBase}/detalhe/${item.id}`;
}

export default function ClubDetailPage({ slug }: Props) {
  const t = siteTexts.clubs;
  const matchesTexts = siteTexts.openMatches;
  const { user } = useAuth();
  const [club, setClub] = useState<Club | null>(null);
  const [upcomingEvents, setUpcomingEvents] = useState<ChallengeHistoryItem[]>([]);
  const [eventsLoading, setEventsLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [upcomingMatches, setUpcomingMatches] = useState<OpenMatchItem[]>([]);
  const [activeSection, setActiveSection] =
    useState<ClubDetailSection>("about");

  useEffect(() => {
    setActiveSection("about");
  }, [slug]);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError("");
    getClubBySlug(slug)
      .then((response) => {
        if (!active) return;
        setClub(response.club);
      })
      .catch(() => {
        if (!active) return;
        setClub(null);
        setError(t.detail.notFound);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [slug, t.detail.notFound]);

  useEffect(() => {
    if (!club?.id) {
      setUpcomingMatches([]);
      return;
    }

    let active = true;
    listPublicMatches()
      .then((response) => {
        if (!active) return;
        setUpcomingMatches(
          listUpcomingOpenMatchesForClub(response.matches || [], club.id).slice(0, 3),
        );
      })
      .catch(() => {
        if (!active) return;
        setUpcomingMatches([]);
      });

    return () => {
      active = false;
    };
  }, [club?.id]);

  useEffect(() => {
    if (!club) {
      setUpcomingEvents([]);
      return;
    }
    let active = true;
    setEventsLoading(true);
    listPublicChallenges({
      status: "all",
      types: ["challenge", "super8", "poco", "tournament"],
    })
      .then((items) => {
        if (!active) return;
        const now = Date.now();
        setUpcomingEvents(
          (Array.isArray(items) ? items : [])
            .filter((item) => isUpcomingClubEvent(item, club, now))
            .sort((left, right) => {
              const leftDate = new Date(String(left.scheduledAt || "")).getTime();
              const rightDate = new Date(String(right.scheduledAt || "")).getTime();
              return leftDate - rightDate;
            })
            .slice(0, 6),
        );
      })
      .catch(() => {
        if (!active) return;
        setUpcomingEvents([]);
      })
      .finally(() => {
        if (active) setEventsLoading(false);
      });
    return () => {
      active = false;
    };
  }, [club]);

  const contacts = useMemo(() => (club ? contactItems(club) : []), [club]);
  const mapEmbedUrl = useMemo(
    () =>
      club && !canResolveClubMapPosition(club) ? buildClubMapEmbedUrl(club) : "",
    [club],
  );
  const matchesHref = useMemo(
    () => (club ? `#/partidas?club=${club.id}` : "#/partidas"),
    [club],
  );
  const createMatchHref = useMemo(() => {
    if (!club) return "#/partidas";
    const target = `#/partidas?club=${club.id}&create=1`;
    return user ? target : buildLoginHref(target);
  }, [club, user]);
  const tabLabels = useMemo(
    () => ({
      about: t.detail.tabs.about,
      location: t.detail.tabs.location,
      contact: t.detail.tabs.contact,
      matches: t.detail.tabs.matches,
      events: t.detail.tabs.events,
    }),
    [t.detail.tabs],
  );

  if (loading) {
    return (
      <AppLayout title={t.title} subtitle={t.subtitle}>
        <LoadingPage label={siteTexts.common.loading} />
      </AppLayout>
    );
  }

  if (error || !club) {
    return (
      <AppLayout title={t.title} subtitle={t.subtitle}>
        <EmptyState message={error || t.detail.notFound} />
      </AppLayout>
    );
  }

  return (
    <AppLayout
      title={<span className="clubs-page-title">{club.name}</span>}
      subtitle={`${club.city} / ${club.state}`}
    >
      <main className="club-detail">
        <section className="club-detail__hero">
          <img className="club-detail__cover" src={club.coverImageUrl} alt="" />
          <div className="club-detail__identity">
            <img className="club-detail__logo" src={club.logoUrl} alt={club.name} />
            <div>
              <h1>{club.name}</h1>
              <p>{club.address}</p>
              <span>
                {club.city} / {club.state}
              </span>
            </div>
          </div>
        </section>

        <ClubDetailTabs
          activeSection={activeSection}
          ariaLabel={t.detail.tabs.navigation}
          labels={tabLabels}
          onChange={setActiveSection}
        />

        <section className="club-detail__content">
          {activeSection === "about" ? (
          <article
            className="club-detail__panel"
            id="club-detail-panel-about"
            role="tabpanel"
            aria-labelledby="club-detail-tab-about"
          >
            <h2>{t.detail.about}</h2>
            <p>{club.description || t.detail.noDescription}</p>
            <ClubActionButtons club={club} />
          </article>
          ) : null}

          {activeSection === "matches" ? (
          <article
            className="club-detail__panel club-detail__panel--matches"
            id="club-detail-panel-matches"
            role="tabpanel"
            aria-labelledby="club-detail-tab-matches"
          >
            <div className="club-detail__panel-header">
              <div>
                <h2>{t.detail.matchesTitle}</h2>
                {!upcomingMatches.length ? <p>{t.detail.noMatches}</p> : null}
              </div>
              <div className="club-detail__matches-actions">
                <Button href={matchesHref} variant="pill">
                  {t.detail.viewAllMatches}
                </Button>
                <Button href={createMatchHref}>{t.detail.createMatch}</Button>
              </div>
            </div>

            {upcomingMatches.length ? (
              <div className="club-detail__matches-list">
                {upcomingMatches.map((match) => (
                  <a
                    key={match.id}
                    className="club-detail__match-item"
                    href={`#/partidas/${match.id}`}
                  >
                    <div className="club-detail__match-copy">
                      <strong>{resolveOpenMatchDisplayTitle(match)}</strong>
                      <span>{formatOpenMatchScheduleRange(match)}</span>
                      <small>{formatOpenMatchCategories(match.categories)}</small>
                    </div>
                    <span className="club-detail__match-spots">
                      {resolveOpenMatchAvailableSlots(match)} {matchesTexts.card.availableSlots}
                    </span>
                  </a>
                ))}
              </div>
            ) : null}
          </article>
          ) : null}

          {activeSection === "contact" ? (
          <article
            className="club-detail__panel"
            id="club-detail-panel-contact"
            role="tabpanel"
            aria-labelledby="club-detail-tab-contact"
          >
            <h2>{t.detail.contact}</h2>
            {contacts.length ? (
              <dl className="club-detail__data">
                {contacts.map((item) => (
                  <div key={item.label}>
                    <dt>{item.label}</dt>
                    <dd>{item.value}</dd>
                  </div>
                ))}
              </dl>
            ) : (
              <p>{t.detail.noContact}</p>
            )}
          </article>
          ) : null}

          {activeSection === "location" ? (
          <article
            className="club-detail__panel club-detail__panel--location"
            id="club-detail-panel-location"
            role="tabpanel"
            aria-labelledby="club-detail-tab-location"
          >
            <div className="club-detail__location-layout">
              <div className="club-detail__address-block">
                <h2>{t.detail.address}</h2>
                <dl className="club-detail__data">
                  <div>
                    <dt>{t.detail.street}</dt>
                    <dd>
                      {[club.address, club.number].filter(Boolean).join(", ")}
                    </dd>
                  </div>
                  <div>
                    <dt>{t.detail.neighborhood}</dt>
                    <dd>
                      {club.neighborhood || siteTexts.common.category.notInformed}
                    </dd>
                  </div>
                  <div>
                    <dt>{t.detail.cityState}</dt>
                    <dd>
                      {club.city} / {club.state}
                    </dd>
                  </div>
                  <div>
                    <dt>{t.detail.zipCode}</dt>
                    <dd>{club.zipCode || siteTexts.common.category.notInformed}</dd>
                  </div>
                </dl>
              </div>
              <div className="club-detail__map-block">
                <h2>{t.mapView}</h2>
                {canResolveClubMapPosition(club) ? (
                  <ClubDetailMap club={club} />
                ) : mapEmbedUrl ? (
                  <div className="club-detail__map">
                    <iframe
                      className="club-detail__map-embed"
                      src={mapEmbedUrl}
                      title={`${t.mapView} - ${club.name}`}
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                    />
                  </div>
                ) : (
                  <div className="club-detail__map">
                    <div className="club-detail__map-empty">
                      <span
                        className="material-symbols-outlined"
                        aria-hidden="true"
                      >
                        location_off
                      </span>
                      <strong>{club.name}</strong>
                      <p>
                        {[club.address, club.city, club.state]
                          .filter(Boolean)
                          .join(", ")}
                      </p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </article>
          ) : null}

          {activeSection === "events" ? (
          <article
            className="club-detail__panel club-detail__panel--events"
            id="club-detail-panel-events"
            role="tabpanel"
            aria-labelledby="club-detail-tab-events"
          >
            <div className="club-detail__section-heading">
              <div>
                <h2>{t.detail.upcomingEventsTitle}</h2>
                <p>{t.detail.upcomingEventsSubtitle}</p>
              </div>
              <span className="material-symbols-outlined" aria-hidden="true">
                event_available
              </span>
            </div>
            {eventsLoading ? (
              <div className="club-detail__events-empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  progress_activity
                </span>
                <strong>{t.detail.loadingEvents}</strong>
              </div>
            ) : upcomingEvents.length > 0 ? (
              <div className="club-detail__events-grid">
                {upcomingEvents.map((item) => (
                  <a
                    className="club-detail__event-card"
                    href={eventDetailHref(item)}
                    key={`club-event-${item.id}`}
                  >
                    <span className="club-detail__event-type">
                      {challengeTypeLabel(item.challengeType)}
                    </span>
                    <strong>{item.challengeName || item.groupName}</strong>
                    <span className="club-detail__event-date">
                      <span className="material-symbols-outlined" aria-hidden="true">
                        calendar_month
                      </span>
                      {formatEventDateRangeLabel(
                        item.scheduledAt,
                        item.scheduledEndAt || "",
                      ) || t.detail.dateFallback}
                    </span>
                  </a>
                ))}
              </div>
            ) : (
              <div className="club-detail__events-empty">
                <span className="material-symbols-outlined" aria-hidden="true">
                  event_busy
                </span>
                <strong>{t.detail.noUpcomingEvents}</strong>
              </div>
            )}
          </article>
          ) : null}
        </section>
      </main>
    </AppLayout>
  );
}
